// components/common/WorkspaceAutocomplete.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, SxProps, Theme } from "@mui/material";
import MuiAutoComplete from "./MuiAutoComplete";

export interface Option {
  label: string;
  value: string;
}

interface Props {
  name?: string;
  label?: string;
  error?: boolean;
  loading?: boolean;
  options: Option[];
  disabled?: boolean;
  inputValue?: string;
  debounceMs?: number;
  sx?: SxProps<Theme>;
  filterOptions?: boolean;
  placeholder?: string;
  value: Option | null;
  helperText?: React.ReactNode;
  onChange?: (option: Option | null) => void;
  onInputChange?: (newValue: string) => void;
}

export default function MuiAutoCompleteWraper({
  sx,
  name,
  error,
  label,
  value,
  options,
  onChange,
  helperText,
  filterOptions,
  inputValue,
  onInputChange,
  loading = false,
  disabled = false,
  debounceMs = 700,
  placeholder = "انتخاب کنید",
}: Props) {
  const [internalInput, setInternalInput] = useState<string>(inputValue ?? "");

  useEffect(() => {
    if (typeof inputValue === "string") setInternalInput(inputValue);
  }, [inputValue]);

  const debouncedValue = useDebounce(internalInput, debounceMs);

  useEffect(() => {
    if (onInputChange) onInputChange(debouncedValue);
  }, [debouncedValue, onInputChange]);

  return (
    <Autocomplete
      inputValue={internalInput}
      options={options}
      value={value}
      noOptionsText={loading ? "درحال بارگذاری..." : "موردی یافت نشد"}
      filterOptions={filterOptions ? (opts) => opts : undefined}
      getOptionLabel={(opt) => opt.label}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input" || reason === "clear" || reason === "reset") {
          setInternalInput(newInputValue || "");
        }
      }}
      disabled={disabled}
      onChange={(_, option) => {
        onChange && onChange(option as Option | null);
      }}
      isOptionEqualToValue={(opt, val) => opt.value === val?.value}
      sx={{
        "& .MuiInputBase-input": { py: "4px" },
        "& .MuiOutlinedInput-root": {
          py: "4px",
        },
        ...((sx as any) || {}),
      }}
      renderInput={(params) => (
        <MuiAutoComplete
          {...params}
          name={name}
          label={label ?? "انتخاب"}
          sx={{ py: "3px" }}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}

function useDebounce<T>(value: T, delay = 700) {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
